"use client"

import { useAddToCart, useAddRentalToCart } from "@/hooks/use-cart-mutations"
import { useCart } from "@/hooks/use-cart"
import { useIntersection } from "@lib/hooks/use-in-view"
import { HttpTypes } from "@medusajs/types"
import { Button } from "@/components/common"
import Divider from "@modules/common/components/divider"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import RentalSection from "@modules/products/components/rental-section"
/** Shallow-compare two option keymaps (Record<string, string>) */
function optionsEqual(
  a: Record<string, string> | undefined,
  b: Record<string, string | undefined>
): boolean {
  if (!a) return false
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every((k) => a[k] === b[k])
}
import { useParams, usePathname, useSearchParams } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import ProductPrice from "../product-price"
import MobileActions from "./mobile-actions"
import { useRouter } from "next/navigation"
import { useTranslations, useLocale } from "next-intl"
import type { RentalInfo } from "@/types/rental"
import { showSuccess, showError } from "@/lib/toast"
import { resolveCartError } from "@lib/util/cart-error-message"
import { convertToLocale } from "@lib/util/money"
import { getProductPrice } from "@lib/util/get-product-price"
import { orderableUnits, resolvePageInStock, type StockFields } from "@lib/util/buy-actions"
import { LOW_STOCK_THRESHOLD } from "@lib/util/product-availability"

type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  rentalInfo?: RentalInfo | null
  disabled?: boolean
}

const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"]
) => {
  return variantOptions?.reduce((acc: Record<string, string>, varopt: any) => {
    acc[varopt.option_id] = varopt.value
    return acc
  }, {})
}

export default function ProductActions({
  product,
  rentalInfo,
  disabled,
}: ProductActionsProps) {
  const t = useTranslations('product')
  const tCart = useTranslations('cart')
  const locale = useLocale()
  const [showRentTooltip, setShowRentTooltip] = useState(false)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { setCart } = useCart()
  const addToCartMutation = useAddToCart()
  const addRentalMutation = useAddRentalToCart()

  const [options, setOptions] = useState<Record<string, string | undefined>>({})
  const [showRentalSection, setShowRentalSection] = useState(
    searchParams.get('rental') === 'true'
  )
  const countryCode = useParams().countryCode as string

  const isAdding = addToCartMutation.isPending
  const isAddingRental = addRentalMutation.isPending

  // If there is only 1 variant, preselect the options
  useEffect(() => {
    if (product.variants?.length === 1) {
      const variantOptions = optionsAsKeymap(product.variants[0].options)
      setOptions(variantOptions ?? {})
    }
  }, [product.variants])

  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return
    }

    return product.variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return optionsEqual(variantOptions, options)
    })
  }, [product.variants, options])

  // update the options when a variant is selected
  const setOptionValue = (optionId: string, value: string) => {
    setOptions((prev) => ({
      ...prev,
      [optionId]: value,
    }))
  }

  //check if the selected options produce a valid variant
  const isValidVariant = useMemo(() => {
    return product.variants?.some((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return optionsEqual(variantOptions, options)
    })
  }, [product.variants, options])

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString())
    const value = isValidVariant ? selectedVariant?.id : null

    if (params.get("v_id") === value) {
      return
    }

    if (value) {
      params.set("v_id", value)
    } else {
      params.delete("v_id")
    }

    router.replace(pathname + "?" + params.toString())
  }, [selectedVariant, isValidVariant])

  // ONE truth: in_stock_now / available_units come from the backend on
  // every /store/products response (the exact rule the add-to-cart
  // refusal runs). resolvePageInStock never short-circuits to "in stock"
  // on manage_inventory=false / allow_backorder when the backend said no;
  // the Medusa heuristic is used only when the response carries no flag.
  const inStock = useMemo(
    () => resolvePageInStock(product as StockFields, selectedVariant),
    [selectedVariant, product]
  )
  // «Осталось N» has to be a number the customer can act on, so it is what one
  // warehouse can ship, not the sum across warehouses (see orderableUnits).
  const unitsLeft = orderableUnits(product as StockFields)
  const showUnitsLeft =
    inStock && typeof unitsLeft === "number" && unitsLeft > 0 && unitsLeft <= LOW_STOCK_THRESHOLD

  const actionsRef = useRef<HTMLDivElement>(null)

  const inView = useIntersection(actionsRef, "0px")

  // add the selected variant to the cart
  const handleAddToCart = () => {
    if (!selectedVariant?.id) return

    addToCartMutation.mutate(
      {
        variantId: selectedVariant.id,
        quantity: 1,
        countryCode,
      },
      {
        onSuccess: () => {
          showSuccess(tCart('addedToCart'), tCart('addedToCartDesc'))
        },
        onError: (error) => {
          // Coded refusals (outOfStock / insufficientInventory / …) render
          // the localized cart.errors.<code> (+ hint); raw text is the
          // fallback only when the backend gave us no code.
          const r = resolveCartError(tCart, error, {
            errorsKey: 'errors',
            fallback: tCart('addFailed'),
          })
          showError(tCart('error'), r.hint ? `${r.description}\n${r.hint}` : r.description)
        },
      }
    )
  }

  // add rental to cart
  const handleAddRental = async (params: {
    variantId: string
    rentalPeriod: number
    rentalUnit: string
    startDate: string
    quantity: number
  }) => {
    addRentalMutation.mutate(
      { ...params, countryCode },
      {
        onSuccess: () => {
          setShowRentalSection(false) // Close section after successful add
          showSuccess(tCart('addedToCart'), tCart('addedToCartDesc'))
        },
        onError: (error) => {
          console.warn('Rental not added to cart:', error)
          const r = resolveCartError(tCart, error, {
            errorsKey: 'errors',
            fallback: tCart('addFailed'),
          })
          showError(tCart('error'), r.hint ? `${r.description}\n${r.hint}` : r.description)
          // Don't close section on error - let user retry
        },
      }
    )
  }

  // Determine button visibility based on availability_mode
  // UX-014: Build buy vs rent comparison tooltip text
  const rentTooltipText = useMemo(() => {
    if (!rentalInfo?.isRentalAvailable || rentalInfo.availabilityMode !== 'both') return null
    const { cheapestPrice } = getProductPrice({ product })
    if (!cheapestPrice?.calculated_price || !rentalInfo.rentalPrice) return null
    const localeMap: Record<string, string> = {
      ru: 'ru-RU', en: 'en-US', cs: 'cs-CZ', uk: 'uk-UA',
    }
    const purchasePrice = cheapestPrice.calculated_price
    // The price is per `rentalUnit`, not per day: a book rented by the month
    // costs 150 CZK/месяц, and «150 CZK/день» is the same number priced 30×
    // cheaper than the shop charges.
    const rentalPrice = convertToLocale({
      amount: rentalInfo.rentalPrice,
      currency_code: rentalInfo.currencyCode,
    })
    const unit = t(`rental.durationUnit.${rentalInfo.rentalUnit}`, { count: 1 })
    return t('rental.buyOrRentTooltip', { purchasePrice, rentalPrice, unit })
  }, [rentalInfo, product, locale, t])

  const showBuyButton = !rentalInfo ||
    !rentalInfo.isRentalAvailable ||
    rentalInfo.availabilityMode === 'sale_only' ||
    rentalInfo.availabilityMode === 'both'

  const showRentButton = rentalInfo?.isRentalAvailable && (
    rentalInfo.availabilityMode === 'rental_only' ||
    rentalInfo.availabilityMode === 'both'
  )

  return (
    <>
      <div className="flex flex-col gap-y-2" ref={actionsRef}>
        <div>
          {(product.variants?.length ?? 0) > 1 && (
            <div className="flex flex-col gap-y-4">
              {(product.options || []).map((option) => {
                return (
                  <div key={option.id}>
                    <OptionSelect
                      option={option}
                      current={options[option.id]}
                      updateOption={setOptionValue}
                      title={option.title ?? ""}
                      data-testid="product-options"
                      disabled={!!disabled || isAdding || isAddingRental}
                    />
                  </div>
                )
              })}
              <Divider />
            </div>
          )}
        </div>

        <ProductPrice product={product} variant={selectedVariant} rentalInfo={rentalInfo} />

        {showUnitsLeft && (
          <p className="text-sm text-muted-foreground" data-testid="units-left">
            {t('leftInStock', { count: unitsLeft })}
          </p>
        )}

        {/* Buttons: Rent (primary) and/or Buy (secondary) based on availability_mode */}
        <div className="flex gap-2">
          {showRentButton && (
            <div
              className="relative flex-1"
              onMouseEnter={() => rentTooltipText && setShowRentTooltip(true)}
              onMouseLeave={() => setShowRentTooltip(false)}
              onFocus={() => rentTooltipText && setShowRentTooltip(true)}
              onBlur={() => setShowRentTooltip(false)}
            >
              <Button
                onClick={() => setShowRentalSection(!showRentalSection)}
                disabled={
                  !selectedVariant ||
                  !!disabled ||
                  isAdding ||
                  isAddingRental ||
                  !isValidVariant ||
                  !inStock
                }
                variant={showRentalSection ? "outline" : "primary"}
                className={showBuyButton ? "w-full h-10" : "w-full h-10"}
                isLoading={isAddingRental}
                data-testid="rent-product-button"
              >
                {!selectedVariant || !isValidVariant
                  ? t('actions.selectVariant')
                  : !inStock
                  ? t('actions.outOfStock')
                  : t('rental.rent')}
              </Button>
              {showRentTooltip && rentTooltipText && (
                <div
                  role="tooltip"
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10 px-3 py-2 bg-popover text-popover-foreground text-xs rounded-md shadow-md border border-border whitespace-nowrap pointer-events-none"
                >
                  {rentTooltipText}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-border" />
                </div>
              )}
            </div>
          )}

          {showBuyButton && (
            <Button
              onClick={handleAddToCart}
              disabled={
                !inStock ||
                !selectedVariant ||
                !!disabled ||
                isAdding ||
                isAddingRental ||
                !isValidVariant
              }
              variant={showRentButton ? "secondary" : "primary"}
              className={showRentButton ? "flex-1 h-10" : "w-full h-10"}
              isLoading={isAdding}
              data-testid="add-product-button"
            >
              {!selectedVariant || !isValidVariant
                ? t('actions.selectVariant')
                : !inStock
                ? t('actions.outOfStock')
                : t('actions.buy')}
            </Button>
          )}
        </div>

        {/* Rental Section - shown when rent button is clicked */}
        {showRentButton && showRentalSection && rentalInfo && selectedVariant && (
          <div id="rental-section">
            <RentalSection
              product={product}
              variant={selectedVariant}
              rentalInfo={rentalInfo}
              onAddRental={handleAddRental}
              isAdding={isAddingRental}
              disabled={!!disabled}
            />
          </div>
        )}

        <MobileActions
          product={product}
          variant={selectedVariant}
          options={options}
          updateOptions={setOptionValue}
          inStock={inStock}
          handleAddToCart={handleAddToCart}
          isAdding={isAdding}
          show={!inView}
          optionsDisabled={!!disabled || isAdding || isAddingRental}
          rentalInfo={rentalInfo}
          onRentClick={() => {
            setShowRentalSection(true)
            setTimeout(() => {
              document.getElementById('rental-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }, 100)
          }}
        />
      </div>
    </>
  )
}
